import { UserInputService } from '@/core/services/api-gateway'
import { generateToast } from '@components/toasting'
import { CustomToastProps } from '@components/toasting/Toast'
import { BaseModule } from '@core/base/module/base-module'
import { AppModule } from '@core/modules/app/app.module'
import { IssuesModule } from '@core/modules/issues'
import { DomainsModule } from '../domains/domains.module'
import { UsersModule } from '../users'

export interface IUserInputsState extends IRemoteData {
    data: TUserInputProcessType
    formData: { actions: SelectionOption[] }
}

const state = {
    loading: false,
    error: false,
    lastFetched: '',
    data: { resolved_issues: [], delete_domains: [], add_domains: [] },
    formData: { actions: [] },
} satisfies IUserInputsState

class UserInputs extends BaseModule<IUserInputsState> {
    constructor(state: IUserInputsState) {
        super(state, 'user-inputs.module')
    }

    async pollProcesses({
        resolvedIssueId,
    }: { resolvedIssueId?: string } = {}) {
        const issuesInProcess: Set<string> = new Set()
        const addDomainInProcess = new Set()
        const deleteDomainInProcess = new Set()

        const listOfProcesses = async (response: TUserInputProcessType) => {
            const isNewResResolveIssuesEmpty =
                response?.resolved_issues?.length === 0
            const isNewResDeleteDomainEmpty =
                response?.delete_domains?.length === 0
            const isNewResAddDomainEmpty = response?.add_domains?.length === 0

            if (resolvedIssueId) {
                issuesInProcess.add(resolvedIssueId)
            }

            response?.resolved_issues?.forEach((issue) => {
                if (issue.external_manager_issue_ids?.length > 0) {
                    issue.external_manager_issue_ids.forEach((id) => {
                        issuesInProcess.add(id)
                    })
                } else {
                    issuesInProcess.add(issue.external_finding_id)
                }
            })

            response?.add_domains?.forEach((domain) => {
                addDomainInProcess.add(domain)
            })

            response?.delete_domains?.forEach((domain) => {
                deleteDomainInProcess.add(domain)
            })

            if (isNewResResolveIssuesEmpty && issuesInProcess.size > 0) {
                await AppModule.refresh()
                issuesInProcess.forEach((issueId) => {
                    const issue =
                        IssuesModule.getIssueById(issueId) ||
                        UsersModule.getUsersIssueById(issueId)

                    if (!issue) return

                    let message = ''
                    let type = 'success'
                    let testId = ''
                    switch (issue?.status) {
                        case 'resolved':
                            message = `Issue was successfully resolved`
                            testId = 'toast-issue-resolved'
                            break
                        case 'in_review':
                            message = `Issue ${issue.name} is being validated by our security team, we'll notify you on the status of your request in the email`
                            testId = 'toast-issue-validating'
                            break
                        case 'open':
                            message = `The issue ${issue.name} was successfully resolved. Note that there are additional assets with this issue still open`
                            testId = 'toast-issue-has-open-assets'
                            break
                        default:
                            message = `Unfortunately the issue was not resolved`
                            type = 'error'
                            testId = 'toast-issue-not-resolved'
                    }

                    generateToast({
                        type: type,
                        data: message,
                        testId,
                    } as CustomToastProps)
                })
            }
            if (isNewResDeleteDomainEmpty && deleteDomainInProcess.size > 0) {
                await AppModule.refresh()
                generateToast({
                    type: 'success',
                    data: `Domains were successfully deleted`,
                    testId: 'toast-domains-deleted',
                })
            }
            if (isNewResAddDomainEmpty && addDomainInProcess.size > 0) {
                await AppModule.refresh()
                generateToast({
                    type: 'success',
                    data: `Domains have been added successfully and the dashboard has been updated.`,
                    testId: 'toast-dashboard-updated',
                })
            }
            IssuesModule.updateInProcessIssuesIds(
                response?.resolved_issues?.flatMap((item) => [
                    ...(item.external_manager_issue_ids ?? []),
                    item.external_finding_id,
                ]) ?? []
            )
            DomainsModule.updateDeletedDomainsAsInProcess(
                response?.delete_domains || []
            )
        }

        if (this.store.get().loading) return

        this.store.setKey('loading', true)
        const results =
            await UserInputService.pollProcessesUntilReady(listOfProcesses)
        if (!results) {
            generateToast({
                type: 'error',
                data: `Service is not available, please try again later`,
            })
        }
        this.store.setKey('loading', false)
    }

    async loadFormData() {
        const store = this.store

        if (!store.get().lastFetched) {
            store.setKey('loading', true)

            const result = await UserInputService.getFormData()

            if (!result) {
                store.setKey('loading', false)
                store.setKey('error', true)
                return false
            }
            store.setKey('formData', result)
            store.setKey('lastFetched', Date.now())
            store.setKey('loading', false)
            return result
        }
        return store.get().formData
    }
}

export const UserInputsModule = new UserInputs(state)
export type TUserInputsModule = typeof UserInputsModule
